/** Material commitments and native purchase handoff for a work-order requirement. */
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/app/auth";
import {
  useChangePartQuantity,
  useRequestPurchase,
  useReservePart,
} from "@/features/inventory/procurementQueries";
import type { Part, WorkOrderDetail } from "@/features/workorders/queries";
import { FormDialog } from "@/shared/components/form-dialog";
import { LocationPicker } from "@/shared/components/pickers";
import { formatQuantity } from "@/shared/lib/format";
import { useForm } from "@/shared/lib/form";
import { Button } from "@/shared/ui/button";
import { Field, Input, Textarea } from "@/shared/ui/form";

type Action = "reserve" | "release" | "quantity" | "purchase";

/** Auditable reservation, release, quantity edit or purchase request. */
function CommitmentDialog({
  wo,
  part,
  action,
  onClose,
}: {
  wo: WorkOrderDetail;
  part: Part;
  action: Action;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const reserve = useReservePart(wo.id);
  const change = useChangePartQuantity(wo.id);
  const purchase = useRequestPurchase();
  const form = useForm({
    initial: {
      quantity:
        action === "release"
          ? "0"
          : action === "purchase"
            ? String(Math.max(0, Number(part.quantity) - Number(part.reserved_quantity)))
            : part.quantity,
      stock_location_id: part.stock_location_id ?? "",
      reason: "",
    },
    idPrefix: "commitment",
  });
  const submit = async (): Promise<void> => {
    const ok = await form.submit(async (v) => {
      if (action === "purchase") {
        await purchase.mutateAsync({
          part_id: part.id,
          quantity: Number(v.quantity),
          reason: v.reason,
        });
      } else if (action === "quantity") {
        await change.mutateAsync({
          partId: part.id,
          body: {
            item_id: part.item_id,
            quantity: Number(v.quantity),
            stock_location_id: part.stock_location_id,
          },
        });
      } else {
        await reserve.mutateAsync({
          partId: part.id,
          body: {
            quantity: action === "release" ? 0 : Number(v.quantity),
            stock_location_id: v.stock_location_id || null,
          },
        });
      }
    });
    if (ok) onClose();
  };
  return (
    <FormDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t(`workOrders.partActions.${action}`)}
      description={`${part.item_sku} · ${part.item_name}`}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
    >
      {action === "release" ? (
        <p>{t("workOrders.partReleaseHint")}</p>
      ) : (
        <Field id="commitment-quantity" label={t("app.quantity")} error={form.errors.quantity}>
          <Input type="number" step="0.001" min="0.001" {...form.field("quantity")} required />
        </Field>
      )}
      {action === "reserve" ? (
        <Field
          id="commitment-stock_location_id"
          label={t("inventory.location")}
          error={form.errors.stock_location_id}
        >
          <LocationPicker
            id="commitment-stock_location_id"
            stockOnly
            siteId={wo.site?.id ?? null}
            value={form.values.stock_location_id}
            onChange={(id) => form.set("stock_location_id", id ?? "")}
            required
          />
        </Field>
      ) : null}
      {action === "purchase" ? (
        <Field
          id="commitment-reason"
          label={t("inventory.procurement.reason")}
          error={form.errors.reason}
        >
          <Textarea {...form.field("reason")} required />
        </Field>
      ) : null}
    </FormDialog>
  );
}

/** Stock commitment quantities stay visible independently of cost/adjustment permissions. */
export function PartCommitmentSummary({ part }: { part: Part }): ReactElement | null {
  const { t } = useTranslation();
  if (!["planned", "reserved"].includes(part.status)) return null;
  return (
    <dl className="space-y-1 text-xs">
      <div>
        <dt className="inline">{t("workOrders.partReserved")}: </dt>
        <dd className="inline">{formatQuantity(part.reserved_quantity)}</dd>
      </div>
      <div>
        <dt className="inline">{t("workOrders.partAvailable")}: </dt>
        <dd className="inline">{formatQuantity(part.available_quantity)}</dd>
      </div>
      <div className={Number(part.shortage_quantity) > 0 ? "font-medium text-destructive" : ""}>
        <dt className="inline">{t("workOrders.partShortage")}: </dt>
        <dd className="inline">{formatQuantity(part.shortage_quantity)}</dd>
      </div>
    </dl>
  );
}

/** Planner/storekeeper controls without general inventory adjustment privileges. */
export function PartCommitmentActions({
  wo,
  part,
}: {
  wo: WorkOrderDetail;
  part: Part;
}): ReactElement | null {
  const { t } = useTranslation();
  const { can } = useAuth();
  const [action, setAction] = useState<Action | null>(null);
  if (
    ["completed", "verified", "closed", "cancelled"].includes(wo.status) ||
    !["planned", "reserved"].includes(part.status)
  )
    return null;
  return (
    <div className="flex flex-wrap justify-end gap-1">
      {action ? (
        <CommitmentDialog wo={wo} part={part} action={action} onClose={() => setAction(null)} />
      ) : null}
      {can("workorders.parts.request") ? (
        <>
          <Button size="sm" variant="ghost" onClick={() => setAction("reserve")}>
            {t("workOrders.partActions.reserve")}
          </Button>
          {Number(part.reserved_quantity) > 0 ? (
            <Button size="sm" variant="ghost" onClick={() => setAction("release")}>
              {t("workOrders.partActions.release")}
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" onClick={() => setAction("quantity")}>
            {t("workOrders.partActions.quantity")}
          </Button>
        </>
      ) : null}
      {can("procurement.request.create") ? (
        <Button size="sm" variant="ghost" onClick={() => setAction("purchase")}>
          {t("workOrders.partActions.purchase")}
        </Button>
      ) : null}
    </div>
  );
}
